import { useSelection } from '../../hooks/PRIVATE_useVisSelection';
import type { ItemInfo } from '../diagram.types';
import { getNavigableInfo } from '../utils/diagramUtils';
import { Ref } from 'preact/hooks';

type SelectionProps<K1, K2, D1, D2> = {
  selectedIds: {
    nodes?: K1[];
    links?: K2[];
  };
  selectionMode: 'none' | 'single' | 'multiple';
  nodeMap: {
    itemIdToDataMap: Map<K1, D1>;
    itemIdToIndexMap: Map<K1, number>;
    itemIndexToIdMap: Map<number, K1>;
  };
  linkMap: {
    itemIdToDataMap: Map<K2, D2>;
    itemIdToIndexMap: Map<K2, number>;
    itemIndexToIdMap: Map<number, K2>;
  };
  focusedItemInfo: ItemInfo<K1, K2>;
  onSelectionChange?: (detail: {
    nodes: K1[];
    links: K2[];
    nodeData: (D1 | undefined)[];
    linkData: (D2 | undefined)[];
  }) => void;
  rootRef: Ref<HTMLDivElement>;
};

type EncodedNodeId = `N_${number}`;
type EncodedLinkId = `L_${number}`;

export function useDiagramSelection<K1, K2, D1, D2>({
  selectedIds,
  selectionMode,
  nodeMap,
  linkMap,
  focusedItemInfo,
  onSelectionChange,
  rootRef
}: SelectionProps<K1, K2, D1, D2>) {
  const nodeIdToIndexMap = nodeMap.itemIdToIndexMap;
  const nodeIndexToIdMap = nodeMap.itemIndexToIdMap;
  const linkIdToIndexMap = linkMap.itemIdToIndexMap;
  const linkIndexToIdMap = linkMap.itemIndexToIdMap;
  const encodeNodeId = (id: K1) => `N_${nodeIdToIndexMap.get(id)}` as EncodedNodeId;
  const decodeNodeId = (id: EncodedNodeId) => nodeIndexToIdMap.get(Number(id.slice(2)));
  const encodeLinkId = (id: K2) => `L_${linkIdToIndexMap.get(id)}` as EncodedLinkId;
  const decodeLinkId = (id: EncodedLinkId) => linkIndexToIdMap.get(Number(id.slice(2)));

  const encodedNodesSelection = selectedIds.nodes ? selectedIds.nodes.map(encodeNodeId) : [];
  const encodedLinksSelection = selectedIds.links ? selectedIds.links.map(encodeLinkId) : [];
  const encodedSelection = [...encodedNodesSelection, ...encodedLinksSelection];
  const idExtracter = (event: Event) => {
    // return encodeNodeId(id) or encodeLinkId(id) depending on node or link selection or undefined if clicked on empty space
    const info =
      event.type === 'keyup'
        ? focusedItemInfo
        : getNavigableInfo<K1, K2>(event.target as HTMLElement, rootRef);
    return info.id !== undefined
      ? info.isNode
        ? encodeNodeId(info.id as K1)
        : encodeLinkId(info.id as K2)
      : undefined;
  };

  const onEncodedSelectionChange = (detail: { ids: (EncodedNodeId | EncodedLinkId)[] }) => {
    const newSelectedNodeIds = detail.ids
      .filter((encId) => encId[0] === 'N')
      .map((encId) => decodeNodeId(encId as EncodedNodeId)!);
    const newSelectedLinkIds = detail.ids
      .filter((encId) => encId[0] === 'L')
      .map((encId) => decodeLinkId(encId as EncodedLinkId)!);

    const nodeData = newSelectedNodeIds.map((selectedId) => {
      return nodeMap.itemIdToDataMap.get(selectedId);
    });
    const linkData = newSelectedLinkIds.map((selectedId) => {
      return linkMap.itemIdToDataMap.get(selectedId);
    });
    return (
      onSelectionChange &&
      onSelectionChange({
        nodes: newSelectedNodeIds,
        links: newSelectedLinkIds,
        nodeData,
        linkData
      })
    );
  };
  const getDataById = (id: EncodedNodeId | EncodedLinkId) => {
    let data;
    if (id[0] === 'N') {
      const nodeId = decodeNodeId(id as EncodedNodeId)!;
      data = nodeMap.itemIdToDataMap.get(nodeId);
    } else if (id[0] === 'L') {
      const linkId = decodeLinkId(id as EncodedLinkId)!;
      data = linkMap.itemIdToDataMap.get(linkId);
    }
    return data;
  };

  const { selectionContent, selectionProps } = useSelection<`N_${number}` | `L_${number}`, D1 | D2>(
    {
      selection: encodedSelection,
      idExtracter,
      selectionMode,
      getDataById,
      onChange: onEncodedSelectionChange
    }
  );

  return { selectionContent, selectionProps };
}
